import { DateTime } from "luxon";
import { useEffect, useRef, useState, type FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";

import {
  type IComment,
  useGetProposalSocialCommentsHttp,
} from "../../../hooks/http/proposal-social-comments/proposal-social-comments.hook";
import { useAddProposalSocialCommentsHttp } from "../../../hooks/http/proposal-social-comments/add-proposal-social-comments.hook";
import Textarea, {
  type TextareaRef,
} from "../../../components/core/textarea.core";
import SendIcon from "../../../assets/svgs/send.svg";
import Button from "../../../components/core/button.core";
import useProposalSocialStore from "../../../store/proposal-social.store";
import useProposalSocialSearchStore from "../../../store/proposal-socials-search.store";
import { useUserStore } from "../../../../store/user.store";

export const CommentsSliderPartial: FunctionComponent = () => {
  const { proposalSocialId, proposalId } = useParams();
  const textareaRef = useRef<TextareaRef>(null);
  const [comments, setComments] = useState<Array<IComment>>([]);
  const { user } = useUserStore();
  const {
    execute: getProposalSocialCommentsHttp,
    fetching: fetchingProposalSocialComments,
  } = useGetProposalSocialCommentsHttp();
  const { toast } = useToast();
  const { updateCommentCount, markCommentsRead } = useProposalSocialStore();
  const {
    updateCommentCount: updateCommentCountSearch,
    markCommentsRead: markCommentsReadSearch,
  } = useProposalSocialSearchStore();

  const [message, setMessage] = useState("");

  const {
    execute: addProposalSocialCommentHttp,
    fetching: fetchingAddProposalSocialComment,
  } = useAddProposalSocialCommentsHttp();

  async function addProposalSocialComment() {
    if (!proposalSocialId || !proposalId) {
      return;
    }
    if (!message.trim()) {
      return;
    }
    const response = await addProposalSocialCommentHttp({
      body: {
        message,
      },
      pathParams: { proposalId, proposalSocialId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setComments([...comments, response.data.comment]);
    updateCommentCount(proposalSocialId, comments.length + 1);
    updateCommentCountSearch(proposalSocialId, comments.length + 1);
    markCommentsRead(proposalSocialId, user?.id || "");
    markCommentsReadSearch(proposalSocialId, user?.id || "");
    setMessage("");
    (textareaRef.current as unknown as HTMLTextAreaElement)?.focus();
    (textareaRef.current as unknown as HTMLTextAreaElement)?.blur();
    getProposalSocialComments();
  }

  async function getProposalSocialComments() {
    if (!proposalSocialId || !proposalId) {
      return;
    }
    const response = await getProposalSocialCommentsHttp({
      body: null,
      pathParams: { proposalId, proposalSocialId },
      queryParams: {
        markRead: true,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setComments(response.data.comments);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getProposalSocialComments();
  }, [proposalSocialId, proposalId]);

  if (fetchingProposalSocialComments) {
    return (
      <div className="flex justify-center items-center w-full h-full text-black">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col w-full h-full font-cabinetgrotesk text-black">
      <div className="top-0 sticky flex items-center gap-5 mb-6 px-4 py-5 border-b border-black w-full">
        <p className="font-semibold text-xl">Comments [{comments.length}]</p>
      </div>

      <div className="flex flex-col flex-1 py-5 overflow-y-auto">
        {comments.map((c) => {
          const userName = c.addedByUser.name;

          // optional: deterministic bg from first letter
          const initial = userName?.charAt(0).toUpperCase();

          return (
            <div key={c.id} className="flex flex-col px-4">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="flex justify-center items-center bg-gray-300 rounded-full w-8 h-8 font-semibold text-[10px] uppercase">
                    {initial}
                  </div>
                  <span className="font-medium text-sm">{userName}</span>
                  {
                    <span className="opacity-30 text-secondary text-xs">
                      {DateTime.fromISO(c.createdAt.toString()).toRelative()}
                    </span>
                  }
                </div>
              </div>
              <div className="mt-4 text-sm leading-6 whitespace-pre-wrap">
                {c.message}
              </div>

              {/* skip for last */}
              {c.id !== comments[comments.length - 1].id && (
                <div className="opacity-10 my-8 border-b border-black" />
              )}
            </div>
          );
        })}

        {!comments.length && (
          <div className="flex flex-1 justify-center items-center text-center text-secondary text-sm">
            No comments yet.
          </div>
        )}
      </div>
      <div className="relative sticky-bottom-0 border-t border-black">
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="px-6 pt-5 border-r-0 border-b-0 border-border border-l-0 rounded-0 h-24 font-semibold placeholder:text-secondary bordr resize-none focus:ring-0 focus:outline-none"
          placeholder="Drop a comment..."
        />
        <div className="right-2 bottom-2 absolute">
          <Button
            isLoading={fetchingAddProposalSocialComment}
            disabled={!message.trim()}
            onClick={addProposalSocialComment}
            className="p-0 w-14 h-14"
          >
            <SendIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};
